import { apiClient } from './client'
import type {
  OrderListResponse,
  OrderDetailDto,
  OrderDocumentDto,
  InvoiceTemplatePayload,
  HandlerCandidateDto,
  CreateInvoiceFromRequestDto,
  ApproveOrderDto,
  UpdatePaymentTermsDto,
  CreatePaymentDto,
  UpdatePaymentDto,
  AssignHandlerDto,
  UpdateOrderStatusDto,
  UpdateInvoiceDto,
  UpdateInvoiceItemDto,
  ListOrdersQueryDto,
  UpdateOrderPositionDto,
  AddOrderPositionsDto,
  CreateOrderPositionDto,
  RemoveOrderPositionsDto,
  AddableOrderPositionDto,
  UnfInvoiceInput,
  UnfInvoicePreviewDto,
  UnfInvoiceApplyResultDto,
  UnfInvoiceReviewDto,
  UnfInvoiceNewLine,
} from '@crm/shared'

type Envelope<T> = { success: true; data: T }

export interface CreateInvoiceResult {
  order: OrderDetailDto
  invoiceId: string
  template: InvoiceTemplatePayload
  created: boolean
}

export async function getOrders(
  query: Partial<ListOrdersQueryDto> = {},
): Promise<OrderListResponse> {
  const res = await apiClient.get<Envelope<OrderListResponse>>('/orders', { params: query })
  return res.data.data
}

export async function getOrder(id: string): Promise<OrderDetailDto> {
  const res = await apiClient.get<Envelope<OrderDetailDto>>(`/orders/${id}`)
  return res.data.data
}

/** Удаляет заказ целиком (заявка и её статусы не меняются). */
export async function deleteOrder(id: string): Promise<void> {
  await apiClient.delete(`/orders/${id}`)
}

export async function getInvoiceTemplate(
  orderId: string,
  invoiceId: string,
): Promise<InvoiceTemplatePayload> {
  const res = await apiClient.get<Envelope<InvoiceTemplatePayload>>(
    `/orders/${orderId}/invoices/${invoiceId}/template`,
  )
  return res.data.data
}

/** Сводный шаблон по всем активным счетам заказа — один Excel для УНФ. */
export async function getCombinedInvoiceTemplate(orderId: string): Promise<InvoiceTemplatePayload> {
  const res = await apiClient.get<Envelope<InvoiceTemplatePayload>>(
    `/orders/${orderId}/combined-template`,
  )
  return res.data.data
}

/** «Выгрузить шаблон для счёта в УНФ»: создаёт/пополняет заказ + создаёт счёт. */
export async function createInvoiceFromRequest(
  requestId: string,
  dto: CreateInvoiceFromRequestDto,
): Promise<CreateInvoiceResult> {
  const res = await apiClient.post<Envelope<CreateInvoiceResult>>(
    `/orders/from-request/${requestId}`,
    dto,
  )
  return res.data.data
}

export async function approveOrder(id: string, dto: ApproveOrderDto): Promise<OrderDetailDto> {
  const res = await apiClient.post<Envelope<OrderDetailDto>>(`/orders/${id}/approve`, dto)
  return res.data.data
}

export async function createPayment(id: string, dto: CreatePaymentDto): Promise<OrderDetailDto> {
  const res = await apiClient.post<Envelope<OrderDetailDto>>(`/orders/${id}/payments`, dto)
  return res.data.data
}

export async function updatePayment(
  paymentId: string,
  dto: UpdatePaymentDto,
): Promise<OrderDetailDto> {
  const res = await apiClient.patch<Envelope<OrderDetailDto>>(`/orders/payments/${paymentId}`, dto)
  return res.data.data
}

export async function deletePayment(paymentId: string): Promise<OrderDetailDto> {
  const res = await apiClient.delete<Envelope<OrderDetailDto>>(`/orders/payments/${paymentId}`)
  return res.data.data
}

export async function assignHandler(id: string, dto: AssignHandlerDto): Promise<OrderDetailDto> {
  const res = await apiClient.patch<Envelope<OrderDetailDto>>(
    `/orders/${id}/positions/handler`,
    dto,
  )
  return res.data.data
}

export async function getHandlerCandidates(): Promise<HandlerCandidateDto[]> {
  const res = await apiClient.get<Envelope<HandlerCandidateDto[]>>('/orders/handler-candidates')
  return res.data.data
}

export async function updateOrderStatus(
  id: string,
  dto: UpdateOrderStatusDto,
): Promise<OrderDetailDto> {
  const res = await apiClient.patch<Envelope<OrderDetailDto>>(`/orders/${id}/status`, dto)
  return res.data.data
}

export async function updateInvoice(
  invoiceId: string,
  dto: UpdateInvoiceDto,
): Promise<OrderDetailDto> {
  const res = await apiClient.patch<Envelope<OrderDetailDto>>(`/orders/invoices/${invoiceId}`, dto)
  return res.data.data
}

export async function deleteInvoice(invoiceId: string): Promise<OrderDetailDto> {
  const res = await apiClient.delete<Envelope<OrderDetailDto>>(`/orders/invoices/${invoiceId}`)
  return res.data.data
}

/** Правка позиции счёта (В2): цена и/или количество. Бэк пересчитывает сумму
 *  счёта и статус оплаты заказа. */
export async function updateInvoiceItem(
  itemId: string,
  patch: UpdateInvoiceItemDto,
): Promise<OrderDetailDto> {
  const res = await apiClient.patch<Envelope<OrderDetailDto>>(
    `/orders/invoice-items/${itemId}`,
    patch,
  )
  return res.data.data
}

export async function uploadOrderDocument(
  orderId: string,
  file: File,
  kind: string,
  invoiceId?: string,
): Promise<OrderDocumentDto> {
  const form = new FormData()
  form.append('kind', kind)
  if (invoiceId) form.append('invoiceId', invoiceId)
  form.append('file', file)
  const res = await apiClient.post<Envelope<OrderDocumentDto>>(
    `/orders/${orderId}/documents`,
    form,
    {
      headers: { 'Content-Type': 'multipart/form-data' },
    },
  )
  return res.data.data
}

export async function deleteOrderDocument(documentId: string): Promise<void> {
  await apiClient.delete(`/orders/documents/${documentId}`)
}

// ─── Редактирование оформленного заказа (29.09.2026, docs/features/order-editing.md) ───

/** Правка позиции заказа: артикул, бренд, количество, цена клиенту. */
export async function updateOrderPosition(
  orderId: string,
  requestItemId: string,
  dto: UpdateOrderPositionDto,
): Promise<OrderDetailDto> {
  const res = await apiClient.patch<Envelope<OrderDetailDto>>(
    `/orders/${orderId}/positions/${requestItemId}`,
    dto,
  )
  return res.data.data
}

/** Позиции заявки, которых нет в заказе, — с ценой из последнего КП. */
export async function getAddableOrderPositions(
  orderId: string,
): Promise<AddableOrderPositionDto[]> {
  const res = await apiClient.get<Envelope<AddableOrderPositionDto[]>>(
    `/orders/${orderId}/addable-positions`,
  )
  return res.data.data
}

/** Добавить в заказ позиции заявки из КП (в выбранный счёт или без счёта). */
export async function addOrderPositions(
  orderId: string,
  dto: AddOrderPositionsDto,
): Promise<OrderDetailDto> {
  const res = await apiClient.post<Envelope<OrderDetailDto>>(`/orders/${orderId}/positions`, dto)
  return res.data.data
}

/** Новая позиция вручную — в заявке сразу «Счёт». */
export async function createOrderPosition(
  orderId: string,
  dto: CreateOrderPositionDto,
): Promise<OrderDetailDto> {
  const res = await apiClient.post<Envelope<OrderDetailDto>>(
    `/orders/${orderId}/positions/new`,
    dto,
  )
  return res.data.data
}

/** Убрать позиции из заказа: «Отказ клиента» или возврат в «КП выставлено». */
export async function removeOrderPositions(
  orderId: string,
  dto: RemoveOrderPositionsDto,
): Promise<OrderDetailDto> {
  const res = await apiClient.post<Envelope<OrderDetailDto>>(
    `/orders/${orderId}/positions/remove`,
    dto,
  )
  return res.data.data
}

/** Заказ по заявке (null — заказа нет) — панель «Открыть заказ» в карточке заявки. */
export async function getOrderIdByRequest(requestId: string): Promise<string | null> {
  const res = await apiClient.get<Envelope<{ orderId: string | null }>>(
    `/orders/by-request/${requestId}`,
  )
  return res.data.data.orderId
}

/** Условия оплаты заказа; предоплата меньше 50 % отправляет заказ на согласование РОП. */
export async function updatePaymentTerms(
  id: string,
  dto: UpdatePaymentTermsDto,
): Promise<OrderDetailDto> {
  const res = await apiClient.patch<Envelope<OrderDetailDto>>(`/orders/${id}/payment-terms`, dto)
  return res.data.data
}

/** Счёт из 1С: какая заявка подходит и что изменится (docs/features/unf-invoice-upload.md). */
export type UnfInvoiceLink = { no: number; itemId: string }

export async function previewUnfInvoice(
  invoice: UnfInvoiceInput,
  requestId?: string,
  links: UnfInvoiceLink[] = [],
): Promise<UnfInvoicePreviewDto> {
  const res = await apiClient.post<Envelope<UnfInvoicePreviewDto>>('/orders/unf-invoice/preview', {
    invoice,
    ...(requestId ? { requestId } : {}),
    ...(links.length > 0 ? { links } : {}),
  })
  return res.data.data
}

/**
 * Записать счёт из 1С в заказ (21.10.2026): заказ подстраивается под счёт. `links` —
 * «та же позиция, что №…», `newLines` — новые позиции с квотой менеджера.
 */
export async function applyUnfInvoice(
  invoice: UnfInvoiceInput,
  requestId: string,
  opts: {
    fromOrder?: boolean
    moreInvoices?: boolean
    links?: UnfInvoiceLink[]
    newLines?: UnfInvoiceNewLine[]
  } = {},
): Promise<UnfInvoiceApplyResultDto> {
  const res = await apiClient.post<Envelope<UnfInvoiceApplyResultDto>>(
    '/orders/unf-invoice/apply',
    { invoice, requestId, ...opts },
  )
  return res.data.data
}

/** Отметка заказа «будут ещё счета» из 1С (та же, что галочка в окне загрузки счёта). */
export async function setOrderMoreInvoices(
  orderId: string,
  value: boolean,
): Promise<OrderDetailDto> {
  const res = await apiClient.patch<Envelope<OrderDetailDto>>(`/orders/${orderId}/more-invoices`, {
    value,
  })
  return res.data.data
}

/** «Счета на проверке»: отложить счёт, который не сошёлся на 100%. */
export async function deferUnfInvoice(
  invoice: UnfInvoiceInput,
  requestId?: string,
): Promise<UnfInvoiceReviewDto> {
  const res = await apiClient.post<Envelope<UnfInvoiceReviewDto>>('/orders/unf-invoice/reviews', {
    invoice,
    ...(requestId ? { requestId } : {}),
  })
  return res.data.data
}

export async function listUnfInvoiceReviews(): Promise<UnfInvoiceReviewDto[]> {
  const res = await apiClient.get<Envelope<UnfInvoiceReviewDto[]>>('/orders/unf-invoice/reviews')
  return res.data.data
}

/** «Убрать» отложенный счёт из списка. */
export async function dismissUnfInvoiceReview(id: string): Promise<void> {
  await apiClient.delete(`/orders/unf-invoice/reviews/${id}`)
}
